import { useState } from "react";
import CreatableSelect from "react-select/creatable";

function formatPluralDuration(num: number, label: "hour" | "minute" | "day") {
	return num === 1 ? `${num} ${label}` : `${num} ${label}s`;
}

interface DurationDropdownElement {
	label: JSX.Element | string;
	value: number;
}

export function DayHourMinutePicker(props: {
	duration_ms: number;
	updateFunc: (value: number) => void;
}) {
	const minutes_in_ms = 1000 * 60;
	const hours_in_ms = minutes_in_ms * 60;
	const days_in_ms = hours_in_ms * 24;

	const days = Math.floor(props.duration_ms / days_in_ms);
	const hours = Math.floor((props.duration_ms % days_in_ms) / hours_in_ms);
	const minutes = (props.duration_ms % hours_in_ms) / minutes_in_ms;

	return (
		<div className="ml-auto flex w-full items-center gap-2">
			<div className="">
				<DurationDropdown
					key={"day"}
					setValue={(val) => {
						props.updateFunc(
							val * days_in_ms +
								hours * hours_in_ms +
								minutes_in_ms * minutes
						);
					}}
					value={days}
					type="day"
				/>
			</div>
			<div className="">
				<DurationDropdown
					key="hour"
					setValue={(val) =>
						props.updateFunc(
							days_in_ms * days +
								val * hours_in_ms +
								minutes_in_ms * minutes
						)
					}
					value={hours}
					type="hour"
				/>
			</div>
			<div className="">
				<DurationDropdown
					key="minute"
					setValue={(val) =>
						props.updateFunc(
							days_in_ms * days +
								hours * hours_in_ms +
								val * minutes_in_ms
						)
					}
					value={minutes}
					type="minute"
				/>
			</div>
		</div>
	);
}

function createInitialDayOptions(val: number) {
	const day_options: { label: JSX.Element | string; value: number }[] = [];
	for (let i = 0; i < 100; i++) {
		day_options.push({
			label: formatPluralDuration(i, "day"),
			value: i,
		});
	}

	if (val >= 100) {
		day_options.push({
			label: formatPluralDuration(val, "day"),
			value: val,
		});
	}

	return day_options;
}

function createInitialHourOptions(val: number) {
	const hour_options: { label: JSX.Element | string; value: number }[] = [];
	for (let i = 0; i < 24; i++) {
		hour_options.push({
			label: formatPluralDuration(i, "hour"),
			value: i,
		});
	}

	if (val >= 24) {
		hour_options.push({
			label: formatPluralDuration(val, "hour"),
			value: val,
		});
	}

	return hour_options;
}

function createInitialMinuteOptions(val: number) {
	const minute_options: { label: JSX.Element | string; value: number }[] = [];
	for (let i = 0; i < 60; i += 5) {
		if (val > i && val < i + 5) {
			minute_options.push({
				label: formatPluralDuration(val, "minute"),
				value: val,
			});
		}

		minute_options.push({
			label: formatPluralDuration(i, "minute"),
			value: i,
		});
	}

	return minute_options;
}

const TypeToInitialOptions: Record<
	"hour" | "minute" | "day",
	(val: number) => DurationDropdownElement[]
> = {
	hour: createInitialHourOptions,
	minute: createInitialMinuteOptions,
	day: createInitialDayOptions,
};

function DurationDropdown(props: {
	type: "hour" | "minute" | "day";
	value: number;
	setValue: (num: number) => void;
}) {
	const [options, setOptions] = useState(
		TypeToInitialOptions[props.type](props.value)
	);

	const selected_option = options.find((val) => val.value === props.value);

	return (
		<div className="field w-full">
			<div className="control w-full">
				<CreatableSelect
					classNamePrefix="sesh-dropdown"
					value={selected_option}
					options={options}
					onChange={(selected: DurationDropdownElement) =>
						props.setValue(selected.value)
					}
					isValidNewOption={(inputValue: string) =>
						Boolean(
							tryParseCreatedSelection(inputValue, props.type)
						)
					}
					formatCreateLabel={(inputValue: string) => {
						const new_option_value = tryParseCreatedSelection(
							inputValue,
							props.type
						);

						if (new_option_value == null) {
							return;
						}

						return formatPluralDuration(
							new_option_value,
							props.type
						);
					}}
					onCreateOption={(inputValue: string) => {
						const new_option_value = tryParseCreatedSelection(
							inputValue,
							props.type
						);

						if (new_option_value == null) {
							return;
						}

						const new_option_dropdown = {
							label: formatPluralDuration(
								new_option_value,
								props.type
							),
							value: new_option_value,
						};

						setOptions((options) => {
							return [...options, new_option_dropdown].sort(
								(a, b) => a.value - b.value
							);
						});

						new_option_value *
							1000 *
							60 *
							(props.type === "hour" ? 60 : 1);

						props.setValue(new_option_value);
					}}
					isDisabled={false}
				/>
			</div>
		</div>
	);
}

function tryParseCreatedSelection(
	input: string,
	type: "hour" | "minute" | "day"
) {
	const new_option_value = input
		.split(" ")
		.map((val) => parseInt(val))
		.find((val) => !isNaN(val));

	if (new_option_value === undefined) {
		return;
	}

	if (new_option_value < 0) {
		return;
	}

	if (type === "minute" && new_option_value >= 60) {
		return;
	}

	return new_option_value;
}
